/**
 * SiteFooter — Premium multi-column footer replacing the single-line version.
 */

import COLORS from "../config/colors";

const C = COLORS;

const TRADE_LINKS = [
  { slug: "decks",    label: "Custom Decks" },
  { slug: "concrete", label: "Concrete Flatwork" },
  { slug: "painting", label: "Interior & Exterior Painting" },
  { slug: "roofing",  label: "Roofing" },
];

function LogoMark() {
  return (
    <svg width="28" height="28" viewBox="0 0 32 32" fill="none" stroke={C.honey} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 28L10 14L16 22L22 10L28 28"/>
      <path d="M4 28h24"/>
      <path d="M16 22V28"/>
    </svg>
  );
}

export default function SiteFooter({ market, trade, onTradeSelect, year }) {
  const currentYear = year || new Date().getFullYear();
  const ownerName    = process.env.REACT_APP_OWNER_NAME    || "UltraLife Trades";
  const ownerEmail   = process.env.REACT_APP_OWNER_EMAIL   || "info@ultralifetrades.com";
  const ownerPhone   = process.env.REACT_APP_OWNER_PHONE   || "";
  const ownerWebsite = process.env.REACT_APP_OWNER_WEBSITE || "ultralifetrades.com";

  const serviceArea = market?.serviceArea || [];

  return (
    <footer
      style={{
        background: `linear-gradient(180deg, ${C.bark} 0%, #1a0e07 100%)`,
        color: C.white,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <style>{`
        @media (max-width: 900px) {
          .footer-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 560px) {
          .footer-grid { grid-template-columns: 1fr !important; padding: 40px 20px 32px !important; }
          .footer-bottom { flex-direction: column; text-align: center; }
        }
      `}</style>
      {/* ── Main columns ── */}
      <div
        className="footer-grid"
        style={{
          maxWidth: 1400,
          margin: "0 auto",
          padding: "64px 32px 48px",
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1fr 1fr",
          gap: "40px 32px",
          alignItems: "start",
        }}
      >
        {/* Col 1 — Brand */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <LogoMark />
            <span
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: 1,
                color: C.white,
              }}
            >
              {ownerName.toUpperCase()}
            </span>
          </div>

          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", lineHeight: 1.75, maxWidth: 300, marginBottom: 24 }}>
            Premium home improvement across 28 US metro markets. Fixed-bid contracts — your estimate is your price, no exceptions.
          </p>

          {/* Contact info */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {ownerPhone && (
              <a
                href={`tel:${ownerPhone.replace(/\D/g, "")}`}
                style={{ fontSize: 14, color: C.honey, textDecoration: "none", fontWeight: 600 }}
              >
                {ownerPhone}
              </a>
            )}
            <a
              href={`mailto:${ownerEmail}`}
              style={{ fontSize: 14, color: "rgba(255,255,255,0.60)", textDecoration: "none" }}
            >
              {ownerEmail}
            </a>
            <span style={{ fontSize: 14, color: "rgba(255,255,255,0.40)" }}>
              {ownerWebsite}
            </span>
          </div>

          {/* Trust badges */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 24 }}>
            {["Fixed-Bid"].map(b => (
              <span
                key={b}
                style={{
                  padding: "5px 12px",
                  borderRadius: 20,
                  border: "1px solid rgba(196,145,59,0.4)",
                  color: C.honey,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                }}
              >
                ✓ {b}
              </span>
            ))}
          </div>
        </div>

        {/* Col 2 — Trades */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2.5, color: C.honey, marginBottom: 20 }}>
            Our Services
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
            {TRADE_LINKS
              .filter(t => !market || market.trades?.includes(t.slug))
              .map(t => (
                <li key={t.slug}>
                  <button
                    onClick={() => onTradeSelect?.(t.slug)}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      color: trade?.slug === t.slug
                        ? C.honey
                        : "rgba(255,255,255,0.60)",
                      fontSize: 14,
                      cursor: "pointer",
                      fontFamily: "'DM Sans', sans-serif",
                      textAlign: "left",
                      transition: "color 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = C.white; }}
                    onMouseLeave={e => {
                      e.currentTarget.style.color = trade?.slug === t.slug
                        ? C.honey
                        : "rgba(255,255,255,0.60)";
                    }}
                  >
                    {t.label}
                  </button>
                </li>
              ))}
          </ul>
        </div>

        {/* Col 3 — Service Area */}
        <div>
          {serviceArea.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2.5, color: C.honey, marginBottom: 20 }}>
                Service Area
                {market && (
                  <span style={{ marginLeft: 6, color: "rgba(255,255,255,0.4)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                    · {market.displayName}, {market.state}
                  </span>
                )}
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {serviceArea.slice(0, 8).map(city => (
                  <li key={city} style={{ fontSize: 13, color: "rgba(255,255,255,0.50)" }}>
                    {city}
                  </li>
                ))}
                {serviceArea.length > 8 && (
                  <li style={{ fontSize: 12, color: "rgba(255,255,255,0.30)", fontStyle: "italic" }}>
                    + {serviceArea.length - 8} more cities
                  </li>
                )}
              </ul>
            </>
          )}
        </div>

        {/* Col 4 — Why Us */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2.5, color: C.honey, marginBottom: 20 }}>
            Why UltraLife
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 14 }}>
            {[
              { head: "Fixed-Bid Contracts",   body: "Your estimate is your price. Period." },
              { head: "Permit-Pulled",          body: "Every job is pulled and inspected properly." },
              { head: "15% Overhead Built In",  body: "No hidden charges added after the fact." },
              { head: "AI-Powered Estimates",   body: "Market-specific pricing in 2 minutes." },
            ].map(({ head, body }) => (
              <li key={head}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.white, marginBottom: 2 }}>{head}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", lineHeight: 1.5 }}>{body}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div
        className="footer-bottom"
        style={{
          borderTop: "1px solid rgba(255,255,255,0.08)",
          padding: "20px 32px",
          maxWidth: 1400,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.35)" }}>
          © {currentYear} {ownerName}
          {market && ` · ${market.displayName}, ${market.state}`}
          {" · "}
          <a href="/work-with-us" style={{ color: "rgba(196,145,59,0.8)", textDecoration: "none", fontWeight: 600 }}>
            For Contractors
          </a>
        </span>

        {market?.licenseNote && (
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", maxWidth: 600, textAlign: "right", lineHeight: 1.5 }}>
            {market.licenseNote}
          </span>
        )}
      </div>
    </footer>
  );
}
